// Tiered free gift
//
// The storefront half of the four "Buy X get Y" automatic discounts. Those make
// the Diya set free, but Shopify never adds a "get" item to the cart by itself,
// so this keeps the right number of units in the cart as the subtotal moves:
//
//   spend >= Rs 1,499  ->  1 x 20ml Diya set
//   spend >= Rs 2,999  ->  2 x 20ml Diya set
//   spend >= Rs 4,499  ->  3 x 20ml Diya set
//   spend >= Rs 5,999  ->  4 x 20ml Diya set
//
// Thresholds are in paise, and the gift's own price is left out of the subtotal
// so it can never help the cart reach a tier. Both of those have to match how
// the discounts are set up in the admin.

(() => {
  // BRASS BHRAMA MUHURTA DIYA SET, 20ML.
  const GIFT_VARIANT_ID = 49627141374248;

  // Highest threshold first, so the first match is the best tier.
  const TIERS = [
    {threshold: 599900, quantity: 4},
    {threshold: 449900, quantity: 3},
    {threshold: 299900, quantity: 2},
    {threshold: 149900, quantity: 1}
  ];

  const CART_WRITE_PATHS = ['/cart/add', '/cart/update', '/cart/change', '/cart/clear'];

  // Our own cart calls go through this, so the interceptor below never sees them.
  const nativeFetch = window.fetch.bind(window);

  let syncing = false;

  function isGiftLine(item) {
    return (item.variant_id || item.id) === GIFT_VARIANT_ID;
  }

  // Shopify counts items towards a minimum purchase amount at their full price,
  // before any other discount, so this uses original_line_price rather than the
  // discounted line total.
  function qualifyingSubtotal(items) {
    return items.reduce((total, item) => {
      return isGiftLine(item) ? total : total + item.original_line_price;
    }, 0);
  }

  function desiredQuantity(subtotal) {
    const tier = TIERS.find((candidate) => subtotal >= candidate.threshold);
    return tier ? tier.quantity : 0;
  }

  async function getCart() {
    const response = await nativeFetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Failed to read the cart: ${response.statusText}`);

    return response.json();
  }

  async function writeCart(url, body) {
    const response = await nativeFetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!response.ok) throw new Error(`${url} failed: ${response.statusText}`);

    return response.json();
  }

  // Returns whether the cart actually changed, so callers only redraw when there
  // is something new to show.
  async function syncGift() {
    const cart = await getCart();
    const giftLines = cart.items.filter(isGiftLine);
    const current = giftLines.reduce((total, item) => total + item.quantity, 0);
    const desired = desiredQuantity(qualifyingSubtotal(cart.items));

    if (desired === current) return false;

    if (giftLines.length === 0) {
      await writeCart(`${routes.cart_add_url}.js`, {
        items: [{ id: GIFT_VARIANT_ID, quantity: desired }]
      });

      return true;
    }

    // Set the first gift line to the tier quantity and clear any duplicates.
    const updates = {};

    giftLines.forEach((item, index) => {
      updates[item.key] = index === 0 ? desired : 0;
    });

    await writeCart(`${routes.cart_update_url}.js`, { updates });

    return true;
  }

  // Mirrors CartItems.getSectionsToRender, but skips whatever isn't on this page:
  // only the cart bubble exists outside /cart.
  function cartSectionTargets() {
    const targets = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');

    if (items) targets.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer) targets.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });

    ['cart-icon-bubble', 'cart-live-region-text'].forEach((id) => {
      if (document.getElementById(id)) targets.push({ id: id, section: id, selector: '.shopify-section' });
    });

    // The progress bar is its own section. Its id is prefixed inside a JSON
    // template, so take the real one from the data-id Liquid rendered.
    const progress = document.getElementById('cart-gift-progress');

    if (progress) {
      targets.push({ id: 'cart-gift-progress', section: progress.dataset.id, selector: '#cart-gift-progress' });
    }

    return targets;
  }

  async function getSections(names) {
    const response = await nativeFetch(`${window.location.pathname}?sections=${names.join(',')}`);

    if (!response.ok) throw new Error(`Failed to render sections: ${response.statusText}`);

    return response.json();
  }

  function sectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  function applySections(targets, sections) {
    targets.forEach((target) => {
      const element = document.getElementById(target.id);
      const html = sections[target.section];

      if (!element || !html) return;

      const fresh = sectionInnerHTML(html, target.selector);

      if (fresh !== undefined) element.innerHTML = fresh;
    });
  }

  async function refreshCartSections() {
    const targets = cartSectionTargets();

    if (targets.length === 0) return;

    applySections(targets, await getSections([...new Set(targets.map((target) => target.section))]));
  }

  function isCartWrite(input) {
    const url =
      typeof input === 'string' ? input : input instanceof Request ? input.url : String(input || '');

    return CART_WRITE_PATHS.some((path) => url.includes(path));
  }

  // Every cart write goes through fetch, so patching it catches the quantity
  // inputs, Remove, the product forms and the sticky buy buttons in one place.
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);

    if (syncing || !response.ok || !isCartWrite(args[0])) return response;

    syncing = true;

    try {
      const changed = await syncGift();

      let body = null;

      try {
        body = await response.clone().json();
      } catch (error) {
        // Not JSON, so the caller renders nothing we can hand back.
      }

      const requested = body && body.sections ? Object.keys(body.sections) : [];

      if (requested.length === 0) {
        await refreshCartSections();
        return response;
      }

      // The caller redraws what it asked for, and its copy is stale only if the
      // gift moved. Everything else is ours to redraw - above all the progress
      // bar, which follows the subtotal even when the tier does not change.
      const ours = cartSectionTargets().filter((target) => !requested.includes(target.section));
      const names = changed ? [...new Set([...requested, ...ours.map((t) => t.section)])] : ours.map((t) => t.section);

      if (names.length === 0) return response;

      const sections = await getSections(names);

      applySections(ours, sections);

      if (!changed) return response;

      // Keep the caller's own fields - cart-notification renders from the added
      // line's `key` - and give it the freshly rendered sections.
      body.sections = sections;

      return new Response(JSON.stringify(body), {
        status: response.status,
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('[Tiered free gift]', error);
    } finally {
      syncing = false;
    }

    return response;
  };

  // Buy now is Shopify's dynamic checkout button: it checks out only the form's
  // product and ignores the cart, so the gift would never reach the order. A cart
  // permalink carries both and lands on the same checkout.
  //
  // This only steps in when a gift is actually due. Otherwise the native button
  // runs untouched, because a permalink replaces the cart and there is no reason
  // to throw away what the customer has saved.
  function buyNowPermalink(button) {
    const form = button.closest('form[action*="/cart/add"]');

    if (!form) return null;

    // The quantity input is attached to the form with a `form` attribute rather
    // than nested inside it, so look controls up through form.elements, which
    // covers both. A querySelector on the form misses it and reads as quantity 1.
    const field = (name) => form.elements.namedItem(name);

    // A permalink carries neither line item properties nor a selling plan, so
    // leave those alone rather than silently dropping what the customer entered.
    const sellingPlan = field('selling_plan');

    if (sellingPlan && sellingPlan.value) return null;

    const customised = Array.from(form.elements).some(
      (control) =>
        control.name &&
        control.name.indexOf('properties[') === 0 &&
        (control.type === 'checkbox' ? control.checked : control.value)
    );

    if (customised) return null;

    const config = form.querySelector('.tiered-gift-prices');
    const variantField = field('id');

    if (!config || !variantField) return null;

    let prices;

    try {
      prices = JSON.parse(config.textContent);
    } catch (error) {
      return null;
    }

    const price = prices[variantField.value];

    if (typeof price !== 'number') return null;

    const quantityField = field('quantity');
    const quantity = Math.max(1, parseInt(quantityField && quantityField.value, 10) || 1);

    // The gift never counts towards its own threshold.
    const qualifying = Number(variantField.value) === GIFT_VARIANT_ID ? 0 : price * quantity;
    const gift = desiredQuantity(qualifying);

    if (gift === 0) return null;

    return `${routes.cart_url}/${variantField.value}:${quantity},${GIFT_VARIANT_ID}:${gift}`;
  }

  // Capture, so this runs before the button's own handler.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.shopify-payment-button__button');

      // Wallet buttons (Shop Pay and friends) keep their own express flow.
      if (!button || button.classList.contains('shopify-payment-button__button--branded')) return;

      const permalink = buyNowPermalink(button);

      if (!permalink) return;

      event.preventDefault();
      event.stopPropagation();

      window.location.href = permalink;
    },
    true
  );

  // Nothing has redrawn yet on a fresh page load, so this has to.
  document.addEventListener('DOMContentLoaded', async () => {
    syncing = true;

    try {
      if (await syncGift()) await refreshCartSections();
    } catch (error) {
      console.error('[Tiered free gift]', error);
    } finally {
      syncing = false;
    }
  });
})();
